import React, { useEffect, useRef, useState } from 'react';

/**
 * Drop-in replacement for <input type="date"> in controlled forms — same native
 * control and same dd-mmm-yyyy display, with three fixes.
 *
 * 1. A native date input reports value="" until all three segments are filled,
 *    so a controlled input gets its old value re-applied by React while the
 *    user is halfway through typing, and the browser wipes the segments it had
 *    already accepted. The edit is buffered locally instead, so the DOM value
 *    and the React value stay identical while typing.
 * 2. A short year is expanded when the field is left: typing 26 gives 2026, not
 *    0026. Done on blur only — rewriting mid-edit would reset the browser's own
 *    digit buffer and mangle the year as it is being typed.
 * 3. An empty date input renders the browser's own "dd-----yyyy" hint, which
 *    cannot be restyled or relabelled. While the field is empty and unfocused a
 *    plain text box carrying a real dd-MMM-yyyy placeholder stands in its
 *    place, and hands over to the date control the moment it is focused.
 */

// '0026-07-01' → '2026-07-01'. 00–49 reads as 20xx, 50–99 as 19xx.
const expandShortYear = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return iso;
  const year = parseInt(m[1], 10);
  if (year < 1 || year > 99) return iso;
  const full = year < 50 ? 2000 + year : 1900 + year;
  return `${full}-${m[2]}-${m[3]}`;
};

export default function DateInput({ value, onChange, onFocus, onBlur, ...rest }) {
  const str = value == null ? '' : String(value);
  const [draft, setDraft] = useState(str);
  const [focused, setFocused] = useState(false);
  const editing = useRef(false);
  const dateRef = useRef(null);
  const handOver = useRef(false);

  useEffect(() => {
    if (!editing.current) setDraft(str);
  }, [str]);

  // Focus moved from the placeholder stand-in to the real date control.
  useEffect(() => {
    if (handOver.current && dateRef.current) {
      handOver.current = false;
      dateRef.current.focus();
    }
  }, [focused]);

  const showPlaceholder = !draft && !focused && !rest.disabled && !rest.readOnly;
  if (showPlaceholder) {
    return (
      <input
        {...rest}
        type="text"
        placeholder="dd-MMM-yyyy"
        value=""
        onChange={() => {}}
        onFocus={e => { handOver.current = true; setFocused(true); if (onFocus) onFocus(e); }}
        onMouseDown={() => { handOver.current = true; setFocused(true); }}
      />
    );
  }

  return (
    <input
      {...rest}
      ref={dateRef}
      type="date"
      value={draft}
      onFocus={e => { editing.current = true; setFocused(true); if (onFocus) onFocus(e); }}
      onBlur={e => {
        editing.current = false;
        setFocused(false);
        // Keep a half-typed date the browser never accepted out of the field.
        const typed = expandShortYear(e.target.value) || str;
        setDraft(typed);
        if (typed !== e.target.value && onChange) onChange({ target: { value: typed } });
        if (onBlur) onBlur(e);
      }}
      onChange={e => { setDraft(e.target.value); if (onChange) onChange(e); }}
    />
  );
}
